import { PrimaryButton, Spinner, SpinnerSize, Text } from "@fluentui/react";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { postTogetherCallback } from "store/api/api";
import { setFlottomatDialog } from "store/fleetPage";
import { AppDispatch } from "store/store";
import { handleAxiosError } from "store/util";
import {
  clearTogetherReturnPath,
  consumePendingFlottomat,
  getTogetherReturnPath,
} from "./together";

/**
 * Landing page for the TOGETHER (TIS) OAuth2 redirect. TOGETHER redirects the browser
 * here with ?code=...&state=... after the user granted consent; the page hands the
 * one-time code to the backend (which exchanges it for the tokens server-side) and
 * then returns the user to the page they started from.
 */
const TogetherCallback = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const startedRef = useRef(false);

  useEffect(() => {
    // the authorization code is single-use - guard against the React 18 StrictMode double effect
    if (startedRef.current) return;
    startedRef.current = true;

    const code = searchParams.get("code");
    const state = searchParams.get("state");
    const togetherError = searchParams.get("error");

    if (togetherError) {
      setError(searchParams.get("error_description") || togetherError);
      return;
    }
    if (!code || !state) {
      setError(
        t(
          "bfm.together.missingCode",
          "The TOGETHER response does not contain an authorization code.",
        ),
      );
      return;
    }

    postTogetherCallback(code, state)
      .then(() => {
        const returnPath = getTogetherReturnPath();
        clearTogetherReturnPath();
        // resume the Flottomat action the user triggered before the redirect:
        // reopen the dialog so it re-checks status (now connected) and loads the proposal
        const pending = consumePendingFlottomat();
        if (pending) {
          dispatch(
            setFlottomatDialog({
              isOpen: true,
              fleetId: pending.fleetId,
              vehicleId: pending.vehicleId,
            }),
          );
        }
        navigate(returnPath, { replace: true });
      })
      .catch((err) => {
        handleAxiosError(err);
        setError(
          t("bfm.together.connectFailed", "Connecting to TOGETHER failed."),
        );
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{ padding: "60px 20px", textAlign: "center" }}>
      {error === null ? (
        <Spinner
          size={SpinnerSize.large}
          label={t(
            "bfm.together.connecting",
            "Finishing the TOGETHER connection...",
          )}
        />
      ) : (
        <>
          <Text block variant="large" style={{ marginBottom: 20 }}>
            {error}
          </Text>
          <PrimaryButton
            text={t("bfm.together.backToApp", "Back to the application")}
            onClick={() => {
              clearTogetherReturnPath();
              navigate("/", { replace: true });
            }}
          />
        </>
      )}
    </div>
  );
};

export default TogetherCallback;
